import React, { useState, ChangeEvent, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Typography, Button, Box, Tooltip, TextField } from "@mui/material";
import AddIcon from "../../assets/images/add-icon-white.svg";
import { actions } from "../../slices/projects";
import { makeStyles } from "@mui/styles";
import AppModal from "../../components/AppModal";
import AppTextbox from "../../components/AppTextbox";
import { Scrambling } from "../../declarations/interface";
import { createScrambling, updateScrambling } from "../../slices/project";
import { DEFAULT_SCRAMBLING } from "./ScramblingContainer";

const useStyles = makeStyles((theme) => ({
  input: {
    "& input": {
      height: "38px !important",
    },
  },
}));
const MAX_LIMIT = 250;
type DescriptionErrorKey = "REQUIRED" | "MAX_LIMIT";
type SelectorErrorKey = "REQUIRED" | "MAX_LIMIT" | "INVALID";

type DescriptionError = {
  [key in DescriptionErrorKey]?: string;
};

type SelectorError = {
  [key in SelectorErrorKey]?: string;
};

type ErrorMsg = {
  description: DescriptionError;
  selector: SelectorError;
};

const errorMsg: ErrorMsg = {
  description: {
    REQUIRED: "This field is required",
    MAX_LIMIT: `Maximum limit is ${MAX_LIMIT} characters`,
  },
  selector: {
    REQUIRED: "This field is required",
    MAX_LIMIT: `Maximum limit is ${MAX_LIMIT} characters`,
    INVALID: "Invalid selector",
  },
};

const AddScrambling: React.FC<{
  scrambling?: Scrambling;
  projectId: string;
  onModalClose: () => void;
}> = ({ scrambling, projectId, onModalClose }) => {
  const classes = useStyles();
  const [data, setData] = useState<Scrambling | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectorError, setSelectorError] = useState<
    SelectorErrorKey | undefined
  >();
  const [descriptionError, setDescriptionError] = useState<
    DescriptionErrorKey | undefined
  >();
  const [title, setTitle] = useState("Add Selector");

  const dispatch = useDispatch();

  const handleModalOpen = () => {
    setData({ ...DEFAULT_SCRAMBLING, project_id: projectId });
  };
  const handleModalClose = () => {
    setModalOpen(false);
    onModalClose();
    dispatch(actions.clearStatus());
  };
  const handleSubmit = () => {
    setSubmitted(true);
  };
  const submitData = () => {
    setModalOpen(false);
    setSubmitted(false);
    if (data?.id.length) {
      dispatch(updateScrambling(data));
    } else {
      dispatch(createScrambling(data));
    }
  };
  const handleNameChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof Scrambling
  ) => {
    const value = event.target.value;
    if (field === "description") {
      validateDescription(value);
    }
    if (field === "selector") {
      validateSelector(value);
    }
    setData((prev) => {
      if (prev)
        return {
          ...prev,
          [field]: event.target.value,
        };
    });
  };

  const validateDescription = (description: string) => {
    if (description.length > 250) {
      setDescriptionError("MAX_LIMIT");
    } else if (description.length === 0) {
      setDescriptionError("REQUIRED");
    } else {
      setDescriptionError(undefined);
    }
  };
  const isValidSelector = (selector: string): boolean => {
    try {
      document.querySelector(selector);
      return true;
    } catch (error) {
      return false;
    }
  };
  const validateSelector = (selector: string) => {
    if (selector.length > 250) {
      setSelectorError("MAX_LIMIT");
    } else if (selector.length === 0) {
      setSelectorError("REQUIRED");
    } else if (!isValidSelector(selector)) {
      setSelectorError("INVALID");
    } else {
      setSelectorError(undefined);
    }
  };

  useEffect(() => {
    setData(scrambling);
  }, [scrambling]);

  useEffect(() => {
    if (data) {
      setModalOpen(true);
    }
  }, [data]);

  useEffect(() => {
    if (data?.id.length) {
      setTitle("Update Selector");
    } else {
      setTitle("Add Selector");
    }
  }, [modalOpen, data]);

  useEffect(() => {
    if (submitted) submitData();
  }, [submitted]);

  return (
    <Box>
      <Tooltip title={"Add new scrambling for this project"}>
        <Button
          startIcon={
            <img src={AddIcon} alt="Add Selector" width="14" height="14" />
          }
          variant="contained"
          color="secondary"
          sx={{
            padding: "10px 21px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
            textTransform: "unset !important",
          }}
          aria-label="Add Selector"
          onClick={handleModalOpen}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              lineHeight: "16px",
            }}
          >
            Add Selector
          </Typography>
        </Button>
      </Tooltip>
      <AppModal
        open={modalOpen}
        onClose={handleModalClose}
        header={
          <Typography
            variant="h5"
            color="primaryHighlight.main"
            sx={{ fontWeight: 600 }}
          >
            {title}
          </Typography>
        }
      >
        {data && (
          <Box mb={0.5}>
            <Box>
              <AppTextbox
                placeholder="Enter Description"
                value={data.description}
                onChange={(event) => {
                  handleNameChange(event, "description");
                }}
                classes={{ root: classes.input }}
                error={!!descriptionError}
                helperText={
                  descriptionError ? errorMsg.description[descriptionError] : ""
                }
              />
            </Box>
            <Box mt={2}>
              <AppTextbox
                placeholder="Enter Selector"
                value={data.selector}
                onChange={(event) => {
                  handleNameChange(event, "selector");
                }}
                classes={{ root: classes.input }}
                error={!!selectorError}
                helperText={
                  selectorError ? errorMsg.selector[selectorError] : ""
                }
              />
            </Box>
            <Box mt={2.75}>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  padding: 1.5,
                }}
                onClick={handleSubmit}
                fullWidth
                aria-label={title}
                disabled={!!descriptionError || !!selectorError}
              >
                <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                  {title}
                </Typography>
              </Button>
            </Box>
          </Box>
        )}
      </AppModal>
    </Box>
  );
};

export default AddScrambling;
